/**
 * GlobeControls - Camera and interaction controls for the globe.
 *
 * Handles:
 * - Camera look direction based on target lat/lng
 * - Mouse/touch drag for rotation
 * - Scroll wheel for zoom
 * - Click events for location selection
 */

import { useRef, useEffect, useCallback } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { GLOBE_CONFIG } from '@/domain/globe/constants'

interface GlobeControlsProps {
  /** Target latitude for camera look direction */
  targetLat: number
  /** Target longitude for camera look direction */
  targetLng: number
  /** Camera heading/rotation in degrees */
  heading: number
  /** Field of view (for camera updates) */
  fov: number
  /** Curvature factor (0 = flat, 1 = hemisphere) for look direction adjustment */
  curvature?: number
  /** Callback when user drags to rotate view */
  onRotate?: (deltaLat: number, deltaLng: number) => void
  /** Callback when user scrolls to zoom */
  onZoom?: (delta: number) => void
  /** Callback when user clicks on the globe surface */
  onLocationClick?: (lat: number, lng: number) => void
  /** Callback when user starts interacting (to cancel animations) */
  onInteractionStart?: () => void
  /** Whether controls are enabled */
  enabled?: boolean
}

/**
 * Camera controller component for the globe.
 *
 * Updates camera look direction based on target lat/lng and handles
 * user interactions for rotation and zoom.
 *
 * @example
 * ```tsx
 * <Canvas>
 *   <GlobeControls
 *     targetLat={48.8566}
 *     targetLng={2.3522}
 *     heading={0}
 *     fov={60}
 *     onRotate={(dLat, dLng) => setTarget(lat + dLat, lng + dLng)}
 *     onZoom={(delta) => setZoom(zoom + delta)}
 *   />
 *   <MorphingHemisphere />
 * </Canvas>
 * ```
 */
export function GlobeControls({
  targetLat,
  targetLng,
  heading,
  fov,
  curvature = 1,
  onRotate,
  onZoom,
  onLocationClick,
  onInteractionStart,
  enabled = true,
}: GlobeControlsProps) {
  const { camera, gl } = useThree()

  // Interaction state
  const isDragging = useRef(false)
  const lastPointer = useRef({ x: 0, y: 0 })
  const pointerStart = useRef({ x: 0, y: 0 })
  const hasDragged = useRef(false) // Track if meaningful drag occurred
  const targetDirection = useRef(new THREE.Vector3())

  // Convert target lat/lng to direction vector
  // For the concave globe, the hemisphere is in front of the camera (-Z direction)
  // - Center of view (lat=0, lng=0) maps to [0, 0, -radius]
  // - Latitude rotates up/down (around X axis)
  // - Longitude rotates left/right (around Y axis)
  // The look direction interpolates between sphere and flat positions based on curvature
  useEffect(() => {
    const radius = GLOBE_CONFIG.SPHERE_RADIUS

    // Convert lat/lng to radians
    const latRad = (targetLat * Math.PI) / 180
    const lngRad = (targetLng * Math.PI) / 180

    // Spherical position (curvature = 1)
    const sphereX = radius * Math.cos(latRad) * Math.sin(lngRad)
    const sphereY = radius * Math.sin(latRad)
    const sphereZ = -radius * Math.cos(latRad) * Math.cos(lngRad)

    // Flat position (curvature = 0) - same as MorphingHemisphere shader
    const flatX = (lngRad / Math.PI) * radius
    const flatY = (latRad / (Math.PI / 2)) * radius
    const flatZ = -radius

    // Interpolate based on curvature
    const x = sphereX * curvature + flatX * (1 - curvature)
    const y = sphereY * curvature + flatY * (1 - curvature)
    const z = sphereZ * curvature + flatZ * (1 - curvature)

    targetDirection.current.set(x, y, z)
  }, [targetLat, targetLng, curvature])

  // Update camera FOV
  useEffect(() => {
    if (camera instanceof THREE.PerspectiveCamera) {
      camera.fov = fov
      camera.updateProjectionMatrix()
    }
  }, [camera, fov])

  // Update camera look direction each frame
  useFrame(() => {
    if (!enabled) return

    // Calculate look direction with heading rotation
    const headingRad = (heading * Math.PI) / 180

    // Create rotation matrix for heading
    const rotationMatrix = new THREE.Matrix4()
    rotationMatrix.makeRotationY(headingRad)

    // Apply heading to target direction
    const lookTarget = targetDirection.current.clone()
    lookTarget.applyMatrix4(rotationMatrix)

    // Point camera at target
    camera.lookAt(lookTarget)
  })

  // Mouse/touch event handlers
  const handlePointerDown = useCallback(
    (event: PointerEvent) => {
      if (!enabled) return
      isDragging.current = true
      hasDragged.current = false
      lastPointer.current = { x: event.clientX, y: event.clientY }
      pointerStart.current = { x: event.clientX, y: event.clientY }
      gl.domElement.setPointerCapture(event.pointerId)
      // Cancel any in-progress camera animation
      onInteractionStart?.()
    },
    [enabled, gl.domElement, onInteractionStart]
  )

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      if (!enabled || !isDragging.current || !onRotate) return

      const deltaX = event.clientX - lastPointer.current.x
      const deltaY = event.clientY - lastPointer.current.y
      lastPointer.current = { x: event.clientX, y: event.clientY }

      // Check if we've moved enough from start to count as a drag (5px threshold)
      const totalDeltaX = event.clientX - pointerStart.current.x
      const totalDeltaY = event.clientY - pointerStart.current.y
      if (Math.abs(totalDeltaX) > 5 || Math.abs(totalDeltaY) > 5) {
        hasDragged.current = true
      }

      // Convert pixel movement to lat/lng change
      // Scale by sensitivity and current FOV
      const sensitivity = GLOBE_CONFIG.ROTATION_SENSITIVITY * (fov / GLOBE_CONFIG.DEFAULT_FOV)
      const deltaLng = -deltaX * sensitivity * 100
      const deltaLat = deltaY * sensitivity * 100

      onRotate(deltaLat, deltaLng)
    },
    [enabled, onRotate, fov]
  )

  const handlePointerUp = useCallback(
    (event: PointerEvent) => {
      if (!enabled) return
      isDragging.current = false
      gl.domElement.releasePointerCapture(event.pointerId)
    },
    [enabled, gl.domElement]
  )

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      if (!enabled || !onZoom) return
      event.preventDefault()

      // Cancel any in-progress camera animation
      onInteractionStart?.()

      // Normalize wheel delta
      const delta = -Math.sign(event.deltaY) * GLOBE_CONFIG.ZOOM_SENSITIVITY

      onZoom(delta)
    },
    [enabled, onZoom, onInteractionStart]
  )

  const handleClick = useCallback(
    (event: MouseEvent) => {
      if (!enabled || !onLocationClick) return

      // Don't trigger click if we dragged (moved more than threshold)
      if (hasDragged.current) return

      // Cast ray from camera through click point
      const rect = gl.domElement.getBoundingClientRect()
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1

      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(new THREE.Vector2(x, y), camera)

      // Create a sphere to intersect with
      const sphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), GLOBE_CONFIG.SPHERE_RADIUS)
      const intersectionPoint = new THREE.Vector3()

      if (raycaster.ray.intersectSphere(sphere, intersectionPoint)) {
        // Convert intersection point to lat/lng for the concave globe coordinate system
        // Inverse of the conversion in the useEffect above:
        // x = radius * cos(lat) * sin(lng)
        // y = radius * sin(lat)
        // z = -radius * cos(lat) * cos(lng)
        const radius = GLOBE_CONFIG.SPHERE_RADIUS
        const px = intersectionPoint.x
        const py = intersectionPoint.y
        const pz = intersectionPoint.z

        // Calculate latitude from y
        const lat = Math.asin(py / radius) * (180 / Math.PI)

        // Calculate longitude from x and z
        const lng = Math.atan2(px, -pz) * (180 / Math.PI)

        onLocationClick(lat, lng)
      }
    },
    [enabled, onLocationClick, camera, gl.domElement]
  )

  // Attach event listeners
  useEffect(() => {
    if (!enabled) return

    const domElement = gl.domElement

    domElement.addEventListener('pointerdown', handlePointerDown)
    domElement.addEventListener('pointermove', handlePointerMove)
    domElement.addEventListener('pointerup', handlePointerUp)
    domElement.addEventListener('pointerleave', handlePointerUp)
    domElement.addEventListener('wheel', handleWheel, { passive: false })
    domElement.addEventListener('click', handleClick)

    return () => {
      domElement.removeEventListener('pointerdown', handlePointerDown)
      domElement.removeEventListener('pointermove', handlePointerMove)
      domElement.removeEventListener('pointerup', handlePointerUp)
      domElement.removeEventListener('pointerleave', handlePointerUp)
      domElement.removeEventListener('wheel', handleWheel)
      domElement.removeEventListener('click', handleClick)
    }
  }, [
    enabled,
    gl.domElement,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleWheel,
    handleClick,
  ])

  // This component doesn't render anything - it just controls the camera
  return null
}
